import {
    AutoAwesome as AutoAwesomeIcon,
    CheckCircle as CheckCircleIcon,
    CompareArrows as CompareIcon,
    Download as DownloadIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    History as HistoryIcon,
    Info as InfoIcon,
    Person as PersonIcon,
    RestoreFromTrash as RestoreIcon,
    Upload as UploadIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import historyService from '../../services/HistoryService';
import VttDiffViewer from '../diff/VttDiffViewer';
import { processVttContent, compareVttContents } from '../../utils/vttUtils';
import { parse } from '../../libs/webvttnotes/parser';
import { getProcessStageColor } from '../../utils/processStageUtils';

// Define the save types and their associated icons/colors
const SAVE_TYPES = {
  AUTO: { label: 'Auto Save', icon: AutoAwesomeIcon, color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.08)' },
  MANUAL: { label: 'Manual Save', icon: PersonIcon, color: '#2196f3', bgColor: 'rgba(33, 150, 243, 0.08)' },
  IMPORT: { label: 'Imported', icon: UploadIcon, color: '#9c27b0', bgColor: 'rgba(156, 39, 176, 0.08)' },
  RESTORE: { label: 'Restored', icon: RestoreIcon, color: '#ff9800', bgColor: 'rgba(255, 152, 0, 0.08)' }
};

const VideoHistoryDialog = ({
  open,
  onClose,
  videoId,
  videoData,
  folderPath,
  onRestore,
  onChangesMade 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [compareVersions, setCompareVersions] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [videoInfo, setVideoInfo] = useState(null);
  const [vttContents, setVttContents] = useState({
    older: null,
    newer: null,
    isLoading: false,
    error: null
  });
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);
  const [restoringVersion, setRestoringVersion] = useState(null);
  const [confirmingRestore, setConfirmingRestore] = useState(false);
  const [restoreSuccess, setRestoreSuccess] = useState(null);

  // Process history data to ensure consistent structure
  const processHistoryData = (data) => {
    if (!data) return null;
    
    // Handle different response structures
    const processedData = data.data ? data.data : data;
    
    // Ensure versions array exists
    if (!processedData.versions) {
      return {
        videoId: videoId,
        versions: [],
        latestVersion: null
      };
    }
    
    // Sort versions by number (descending)
    const sortedVersions = [...processedData.versions].sort((a, b) => 
      (b.versionNumber || 0) - (a.versionNumber || 0)
    );
    
    // Ensure each version has an ID
    const versionsWithIds = sortedVersions.map(v => ({
      ...v,
      id: v.id || `version-${v.versionNumber}`
    }));
    
    return {
      ...processedData,
      versions: versionsWithIds,
      latestVersion: processedData.latestVersion || versionsWithIds[0] || null
    };
  };

  // Fetch video languages when dialog opens
  useEffect(() => {
    if (open && videoId) {
      setLoading(true);
      setError('');
      setExpandedRows({});
      setCompareVersions([]);
      setComparing(false); // Reset the comparison view state when dialog opens
      
      const userData = JSON.parse(localStorage.getItem('authUser'));
      if (!userData) {
        setError('Authentication information not found');
        setLoading(false);
        return;
      }
      
      // Fetch video details if not provided
      const fetchVideoData = videoData ? 
        Promise.resolve(videoData) : 
        videoCRUDServiceInstance.getVideoById(videoId, userData.email, userData.access)
          .then(response => response.data)
          .catch(error => {
            console.error('Error fetching video data:', error);
            setError('Failed to load video details');
            throw error;
          });
      
      fetchVideoData
        .then(data => {
          // Extract languages from video data
          const langs = data.videoLanguages || [];
          setLanguages(langs);
          setVideoInfo(data);
          
          // Set first language as default selected language
          if (langs.length > 0) {
            setActiveTab(0);
            setSelectedLanguage(langs[0].languageName);
            
            return historyService.getVideoHistory(videoId, langs[0].languageName);
          }
          return Promise.resolve(null);
        })
        .then(historyData => {
          const processedHistory = processHistoryData(historyData);
          setHistoryData(processedHistory);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error in history fetch workflow:', err);
          setError('Failed to load video history');
          setLoading(false);
        });
    } else {
      // Reset state when dialog closes
      setActiveTab(0);
      setSelectedLanguage(null);
      setHistoryData(null);
      setCompareVersions([]);
      setExpandedRows({});
      setComparing(false); // Also reset when dialog closes
    }
  }, [open, videoId, videoData]);
  
  // Handle language tab change
  const handleTabChange = (event, newValue) => {
    if (newValue === activeTab) return;
    
    setActiveTab(newValue);
    const lang = languages[newValue]?.languageName;
    
    if (lang) {
      setSelectedLanguage(lang);
      setLoading(true);
      setCompareVersions([]);
      setExpandedRows({});
      setComparing(false); // Reset comparison view when changing language
      
      historyService.getVideoHistory(videoId, lang)
        .then(history => {
          const processedHistory = processHistoryData(history);
          setHistoryData(processedHistory);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching language history:', err);
          setError(`Failed to load history for ${lang}`);
          setLoading(false);
        });
    }
  };
  
  // Toggle version selection for comparison
  const toggleVersionForComparison = (version) => {
    if (!version) return;
    
    // Use version number for comparison
    const isSelected = compareVersions.some(v => v.versionNumber === version.versionNumber);
    
    if (isSelected) {
      setCompareVersions(compareVersions.filter(v => v.versionNumber !== version.versionNumber));
    } else {
      if (compareVersions.length < 2) {
        setCompareVersions([...compareVersions, version]);
      } else {
        setCompareVersions([compareVersions[1], version]);
      }
    }
  };
  
  // Handle compare button click
  const handleCompare = () => {
    if (compareVersions.length !== 2) return;
    
    // Sort by version number to ensure older is first
    const sortedVersions = [...compareVersions].sort((a, b) => a.versionNumber - b.versionNumber);
    const [olderVersion, newerVersion] = sortedVersions;
    
    setComparing(true);
    fetchVttContents(olderVersion, newerVersion);
  };
  
  // Handle restore version
  const handleRestore = (version) => {
    if (!version) return;
    
    // Set the version we're working with
    setRestoringVersion(version);
    setConfirmingRestore(true);
  };

  // Confirm restore
  const confirmRestore = () => {
    if (!restoringVersion) return;
    
    setConfirmingRestore(false);
    setRestoring(true);
    setRestoreError(null);
    
    historyService.restoreVersion(
      videoId, 
      selectedLanguage, 
      restoringVersion.versionNumber
    )
    .then(response => {
      // Update the history data with the new version
      setHistoryData(processHistoryData(response.history));
      
      if(onChangesMade) onChangesMade();

      // Show success message
      setRestoreSuccess({
        version: restoringVersion.versionNumber,
        time: new Date().toISOString()
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => setRestoreSuccess(null), 5000);
      
      // If there's a callback for refreshing the parent component, call it
      if (typeof onRestore === 'function') {
        onRestore(selectedLanguage);
      }
    })
    .catch(error => {
      console.error('Error restoring version:', error);
      setRestoreError(`Failed to restore: ${error.response?.data?.message || error.message}`);
    })
    .finally(() => {
      setRestoring(false);
      setRestoringVersion(null);
    });
  };

  // Cancel restore
  const cancelRestore = () => {
    setConfirmingRestore(false);
    setRestoringVersion(null);
  };
  
  // Toggle expanded row
  const toggleExpandRow = (versionId) => {
    setExpandedRows(prev => ({
      ...prev,
      [versionId]: !prev[versionId]
    }));
  };
  
  // Format process stage for display
  const formatProcessStage = (stage) => {
    if (!stage) return 'Unknown';
    
    // Remove the suffixes and format
    let formatted = stage
      .replace('_WIP', ' (In Progress)')
      .replace('_SEVAK_COMPLETE', ' (Complete)')
      .replace('_AI_COMPLETE', ' (AI Complete)')
      .replace('_AI_WIP', ' (AI In Progress)');
      
    // Extract the level number
    const levelMatch = formatted.match(/LEVEL(\d+)/);
    if (levelMatch) {
      return `L${levelMatch[1]}${formatted.split('LEVEL' + levelMatch[1])[1]}`;
    }
    
    return formatted;
  };
  
  
  
  // Get icon and color for save type
  const getSaveTypeIcon = (saveType) => {
    const type = SAVE_TYPES[saveType] || { icon: HistoryIcon, color: '#757575', label: saveType || 'Unknown' };
    const IconComponent = type.icon;
    
    return (
      <Tooltip title={type.label}>
        <IconComponent fontSize="small" sx={{ color: type.color, verticalAlign: 'middle' }} />
      </Tooltip>
    );
  };
  
  // Format time for display
  const formatTime = (timestamp) => {
    if (!timestamp) {
      return { relative: 'Unknown', full: 'Unknown date' };
    }
    
    try {
      const date = new Date(timestamp);
      return {
        relative: formatDistanceToNow(date, { addSuffix: true }),
        full: date.toLocaleString()
      };
    } catch (e) {
      return { relative: 'Unknown', full: 'Unknown date' };
    }
  };
  
  // Fetch and parse VTT files
  const fetchVttContents = async (version1, version2) => {
    if (!version1?.fileUrl || !version2?.fileUrl) {
      setVttContents({
        older: null,
        newer: null,
        diff: null,
        isLoading: false,
        error: 'Missing file URLs for one or both versions'
      });
      return;
    }

    setVttContents(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Fetch both VTT files in parallel
      const [olderResponse, newerResponse] = await Promise.all([
        axios.get(version1.fileUrl),
        axios.get(version2.fileUrl)
      ]);
      
      // Use processVttContent instead of parseVttContent
      const olderContent = processVttContent(olderResponse.data);
      const newerContent = processVttContent(newerResponse.data);
      
      // Process the differences
      const diff = compareVttContents(olderContent, newerContent);
      
      setVttContents({
        older: {
          version: version1,
          content: olderContent,
          raw: olderResponse.data
        },
        newer: {
          version: version2,
          content: newerContent,
          raw: newerResponse.data
        },
        diff,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching VTT files:', error);
      setVttContents({
        older: null,
        newer: null,
        diff: null,
        isLoading: false,
        error: 'Failed to load subtitle files: ' + (error.message || 'Unknown error')
      });
    }
  };

  // Safe access to history data
  const hasHistory = historyData && Array.isArray(historyData.versions) && historyData.versions.length > 0;
  const versionCount = hasHistory ? historyData.versions.length : 0;
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '8px',
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 2
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight="600">
              <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Version History
            </Typography>
            {videoInfo && (
              <Typography variant="subtitle2" color="text.secondary" mt={0.5}>
                {videoInfo.videoName || 'Unknown Video'}
              </Typography>
            )}
          </Box>
          
          {hasHistory && (
            <Chip 
              label={`${versionCount} version${versionCount !== 1 ? 's' : ''}`}
              sx={{
                  color: 'secondary.main'
              }}
              variant="outlined"
              size="small"
            />
          )}
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Loading history...</Typography>
            </Stack>
          </Box>
        ) : error ? (
          <Alert 
            severity="error" 
            variant="outlined"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => {
                setLoading(true);
                setError('');
                historyService.getVideoHistory(videoId, selectedLanguage)
                  .then(data => {
                    setHistoryData(processHistoryData(data));
                    setLoading(false);
                  })
                  .catch(err => {
                    console.error('Error in retry:', err);
                    setError('Failed to load video history');
                    setLoading(false);
                  });
              }}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : (
          <>
            {/* Language Tabs */}
            {languages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Select Language:
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 1,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="language tabs"
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      '& .MuiTab-root': {
                        minHeight: '48px',
                        fontWeight: 400,
                      },
                      '& .Mui-selected': {
                        fontWeight: 600,
                        color: 'primary.main',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      },
                    }}
                    TabIndicatorProps={{
                      style: {
                        height: '3px'
                      }
                    }}
                  >
                    {languages.map((lang, index) => {
                      const isActive = index === activeTab;
                      return (
                        <Tab
                          key={lang.languageName}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="body2" 
                                component="span"
                                sx={{ fontWeight: isActive ? 600 : 400 }}
                              >
                                {lang.languageName}
                              </Typography>
                              {isActive && (
                                <Chip 
                                  label="Current" 
                                  size="small"
                                  variant="outlined" 
                                  sx={{ 
                                    height: '20px',
                                    '& .MuiChip-label': {
                                      px: 0.8,
                                      py: 0,
                                      fontSize: '0.625rem',
                                      fontWeight: 600,
                                      color: 'secondary.main'
                                    }
                                  }} 
                                />
                              )}
                            </Box>
                          }
                          id={`language-tab-${index}`}
                          aria-controls={`language-tabpanel-${index}`}
                          sx={{
                            textTransform: 'none',
                            px: 2,
                            py: 1.5,
                            minWidth: 'auto',
                            borderRight: index < languages.length - 1 ? 1 : 0,
                            borderColor: 'divider',
                          }}
                        />
                      );
                    })}
                  </Tabs>
                </Paper>
              </Box>
            )}

            {/* Selected Language Card */}
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 3,
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              <CardContent sx={{ 
                p: 2, 
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                '&:last-child': { pb: 2 }
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight={500}>
                      {selectedLanguage || (languages[activeTab]?.languageName || "No language")}
                    </Typography>
                  </Box>
                  
                  {hasHistory && (
                    <Typography variant="body2" color="text.secondary">
                      {versionCount} version{versionCount !== 1 ? 's' : ''} available
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Comparison Controls */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: '8px',
                backgroundColor: comparing ? 'rgba(33, 150, 243, 0.04)' : 'transparent'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <CompareIcon color={comparing ? 'primary' : 'action'} />
                <Typography variant="subtitle2" fontWeight={500} color={comparing ? 'primary' : 'text.primary'}>
                  {comparing ? 'Comparing Versions' : 'Select up to 2 versions to compare'}
                </Typography>
              </Stack>
              
              <Stack direction="row" spacing={1} alignItems="center">
                {compareVersions.map((version, index) => (
                  <Chip 
                    key={version.versionNumber || index}
                    label={`Version ${version.versionNumber}`}
                    onDelete={() => toggleVersionForComparison(version)}
                    sx={{
                        color: 'secondary.main'
                    }}
                    size="small"
                    variant={index === 0 ? "outlined" : "filled"}
                  />
                ))}
                
                {compareVersions.length > 0 && (
                  <Button
                    variant="contained"
                    disableElevation
                    size="small"
                    disabled={compareVersions.length !== 2}
                    onClick={handleCompare}
                    startIcon={<CompareIcon />}
                    color="primary"
                  >
                    Compare
                  </Button>
                )}
              </Stack>
            </Paper>
            
            {comparing ? (
              <Card 
                variant="outlined" 
                sx={{ 
                  mb: 3, 
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ 
                    p: 2, 
                    borderBottom: '1px solid', 
                    borderColor: 'divider',
                    backgroundColor: 'rgba(33, 150, 243, 0.04)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="subtitle1" fontWeight={500}>
                      Comparing Versions: {compareVersions[0]?.versionNumber} vs {compareVersions[1]?.versionNumber}
                    </Typography>
                    
                    <Button 
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => setComparing(false)}
                      sx={{ textTransform: 'none' }}
                    >
                      Close Comparison
                    </Button>
                  </Box>
                  
                  <Box sx={{ p: 3 }}>
                    <VttDiffViewer 
                      diff={vttContents.diff}
                      isLoading={vttContents.isLoading}
                      error={vttContents.error}
                      olderVersion={vttContents.older?.version}
                      newerVersion={vttContents.newer?.version}
                      formatTime={formatTime} // Pass the formatTime function to the component
                    />
                  </Box>
                </CardContent>
              </Card>
            ) : hasHistory ? (
              <TableContainer 
                component={Paper} 
                variant="outlined"
                sx={{ 
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                    <TableRow>
                      <TableCell width="8%" sx={{ fontWeight: 600 }}>Version</TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 600 }}>Stage</TableCell>
                      <TableCell width="12%" sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 600 }}>Created By</TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 600 }}>Time</TableCell>
                      <TableCell width="35%" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Current/Latest Version */}
                    {historyData?.latestVersion && (
                      <TableRow 
                        sx={{ 
                          backgroundColor: 'rgba(76, 175, 80, 0.04)',
                          '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' }
                        }}
                      >
                        <TableCell>
                          <Chip 
                            label="Current" 
                            color="success" 
                            size="small" 
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatProcessStage(historyData.latestVersion.processStage) || 'Unknown'}
                            size="small"
                            sx={{ 
                              backgroundColor: getProcessStageColor(historyData.latestVersion.processStage),
                              color: '#fff',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {getSaveTypeIcon(historyData.latestVersion.saveType)}
                            <Typography variant="caption" fontWeight={500}>
                              {SAVE_TYPES[historyData.latestVersion.saveType]?.label || historyData.latestVersion.saveType || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={historyData.latestVersion.createdBy || 'Unknown'}>
                            <Typography variant="body2" noWrap>
                              {(historyData.latestVersion.createdBy || 'Unknown').split('@')[0]}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={formatTime(historyData.latestVersion.createdAt).relative}>
                            <Typography variant="body2">
                              {formatTime(historyData.latestVersion.createdAt).full}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            variant={compareVersions.some(v => v.versionNumber === historyData.latestVersion.versionNumber) ? "contained" : "outlined"}
                            onClick={() => toggleVersionForComparison(historyData.latestVersion)}
                            color="primary"
                            disableElevation
                            sx={{ mr: 1, textTransform: 'none' }}
                            startIcon={<CompareIcon />}
                          >
                            {compareVersions.some(v => v.versionNumber === historyData.latestVersion.versionNumber) ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* History Versions */}
                    {historyData.versions.map((version, index) => (
                      <React.Fragment key={version.versionNumber || index}>
                        <TableRow 
                          sx={{ 
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                            backgroundColor: compareVersions.some(v => v.versionNumber === version.versionNumber) 
                              ? 'rgba(33, 150, 243, 0.08)' 
                              : SAVE_TYPES[version.saveType]?.bgColor || 'transparent',
                            borderTop: index === 0 ? '1px solid rgba(224, 224, 224, 1)' : 'none'
                          }}
                        >
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              fontWeight={500}
                              sx={{ 
                                color: compareVersions.some(v => v.versionNumber === version.versionNumber) ? 'primary.main' : 'text.primary' 
                              }}
                            >
                              v{version.versionNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatProcessStage(version.processStage) || 'Unknown'}
                              size="small"
                              sx={{ 
                                backgroundColor: getProcessStageColor(version.processStage),
                                color: '#fff',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {getSaveTypeIcon(version.saveType)}
                              <Typography variant="caption" fontWeight={500}>
                                {SAVE_TYPES[version.saveType]?.label || version.saveType || 'Unknown'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={version.createdBy || 'Unknown'}>
                              <Typography variant="body2" noWrap>
                                {version.createdBy === 'system' ? 'System' : (version.createdBy || 'Unknown').split('@')[0]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={formatTime(version.createdAt).relative}>
                              <Typography variant="body2">
                                {formatTime(version.createdAt).full}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button 
                                size="small" 
                                variant={compareVersions.some(v => v.versionNumber === version.versionNumber) ? "contained" : "outlined"}
                                onClick={() => toggleVersionForComparison(version)}
                                color="primary"
                                disableElevation
                                startIcon={<CompareIcon />}
                                sx={{ textTransform: 'none' }}
                                disabled={restoring || confirmingRestore} // Disable during restore workflow
                              >
                                {compareVersions.some(v => v.versionNumber === version.versionNumber) ? 'Selected' : 'Select'}
                              </Button>
                              
                              {/* Conditional rendering for restore button states */}
                              {confirmingRestore && restoringVersion?.versionNumber === version.versionNumber ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" fontWeight={500} color="warning.main">
                                    Restore version {version.versionNumber}?
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    onClick={confirmRestore}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={cancelRestore}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                  >
                                    No
                                  </Button>
                                </Box>
                              ) : restoring && restoringVersion?.versionNumber === version.versionNumber ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  sx={{ textTransform: 'none' }}
                                  disabled
                                  startIcon={<CircularProgress size={16} />}
                                >
                                  Restoring...
                                </Button>
                              ) : restoreSuccess?.version === version.versionNumber ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  sx={{ textTransform: 'none' }}
                                  disableElevation
                                  startIcon={<CheckCircleIcon />}
                                >
                                  Restored
                                </Button>
                              ) : (
                                
                                <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleRestore(version)}
                                  color="secondary"
                                  startIcon={<RestoreIcon />}
                                  sx={{ textTransform: 'none' }}
                                  disabled={
                                    restoring || 
                                    confirmingRestore
                                  }
                                >
                                  Restore
                                </Button>
                                </span>
                              )}

                              {/* Error message */}
                              {restoreError && restoringVersion?.versionNumber === version.versionNumber && (
                                <Alert 
                                  severity="error" 
                                  variant="outlined" 
                                  sx={{ 
                                    position: 'absolute',
                                    mt: '40px',
                                    zIndex: 10,
                                    maxWidth: '300px',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                  }} 
                                  onClose={() => setRestoreError(null)}
                                >
                                  {restoreError}
                                </Alert>
                              )}
                              
                              <IconButton 
                                size="small" 
                                onClick={() => toggleExpandRow(version.versionNumber)}
                                aria-label={expandedRows[version.versionNumber] ? "collapse details" : "expand details"}
                                sx={{ 
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: '4px',
                                  p: '4px',
                                  ml: '4px!important'
                                }}
                                disabled={restoring || confirmingRestore} // Disable during restore workflow
                              >
                                {expandedRows[version.versionNumber] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Details */}
                        <TableRow>
                          <TableCell 
                            colSpan={6} 
                            sx={{ 
                              p: 0, 
                              borderBottom: expandedRows[version.versionNumber] ? '1px solid rgba(224, 224, 224, 1)' : 'none' 
                            }}
                          >
                            <Collapse in={expandedRows[version.versionNumber]} timeout="auto" unmountOnExit>
                              <Box sx={{ 
                                p: 2, 
                                backgroundColor: 'rgba(0,0,0,0.01)',
                                borderTop: '1px dashed rgba(224, 224, 224, 1)'
                              }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Paper 
                                      variant="outlined" 
                                      sx={{ 
                                        p: 2, 
                                        backgroundColor: 'white',
                                        height: '100%',
                                        borderRadius: '8px'
                                      }}
                                    >
                                      <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                                        File Information
                                      </Typography>
                                      <Divider sx={{ mb: 2 }} />
                                      <Stack spacing={1.5}>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            File Name
                                          </Typography>
                                          <Typography variant="body2">
                                            {videoInfo?.videoName || 'Unknown'} 
                                          </Typography>
                                        </Box>
                                        
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            ID
                                          </Typography>
                                          <Typography variant="body2" component="div" sx={{ wordBreak: 'break-all' }}>
                                            {historyData.videoId || videoId || 'Unknown'}
                                          </Typography>
                                        </Box>
                                        
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Path
                                          </Typography>
                                          <Typography variant="body2" component="div" sx={{ wordBreak: 'break-all' }}>
                                            {folderPath || 'Unknown'}
                                          </Typography>
                                        </Box>
                                        
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Version
                                          </Typography>
                                          <Typography variant="body2">
                                            {version.versionNumber} of {historyData.versions.length}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Paper 
                                      variant="outlined" 
                                      sx={{ 
                                        p: 2, 
                                        backgroundColor: 'white',
                                        height: '100%',
                                        borderRadius: '8px'
                                      }}
                                    >
                                      <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                                        Version Details
                                      </Typography>
                                      <Divider sx={{ mb: 2 }} />
                                      <Stack spacing={1.5}>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Created By
                                          </Typography>
                                          <Typography variant="body2">
                                            {version.createdBy || 'Unknown'}
                                          </Typography>
                                        </Box>
                                        
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Created At
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatTime(version.createdAt).full}
                                          </Typography>
                                        </Box>
                                        
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Stage
                                          </Typography>
                                          <Chip
                                            label={formatProcessStage(version.processStage) || 'Unknown'}
                                            size="small"
                                            sx={{ 
                                              backgroundColor: getProcessStageColor(version.processStage),
                                              color: '#fff',
                                              fontWeight: 500,
                                              mt: 0.5
                                            }}
                                          />
                                        </Box>
                                        
                                        {version.comment && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                              Comment
                                            </Typography>
                                            <Typography variant="body2">
                                              {version.comment}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                  
                                  <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                      <Button 
                                        variant="outlined" 
                                        href={version.fileUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        startIcon={<DownloadIcon />}
                                        sx={{ textTransform: 'none' }}
                                        disabled={!version.fileUrl}
                                      >
                                        Download VTT File
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card variant="outlined" sx={{ borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  py: 4
                }}>
                  <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                  <Typography color="text.secondary" fontWeight={500} variant="h6" mb={1}>
                    No Version History Found
                  </Typography>
                  <Typography color="text.secondary" variant="body2" align="center" sx={{ maxWidth: '400px' }}>
                    There are no saved versions for this video in {selectedLanguage || 'the selected language'}.
                    Version history will be created when subtitles are edited and saved.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          disableElevation
          sx={{ textTransform: 'none', px: 3 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoHistoryDialog;